/**
 * CommunicatorWidget — globalny pływający widget w stylu Intercom.
 *
 * Renderowany globalnie w MainApp (zawsze widoczny po zalogowaniu).
 * Składa się z:
 *  - FAB (przycisk-kółko w prawym dolnym rogu) z badge'em unread
 *  - Panel popup (desktop: 420×640px w prawym dolnym rogu, mobile: fullscreen)
 *  - 3 sekcje przełączane bottom nav: Główna / Wiadomości / Pomoc
 *
 * Kolory: używa zmiennych CSS aplikacji (var(--bg), var(--bg-card), var(--text)...)
 * Klawiatura ekranowa: ukryta (hideKeyboardToggle = true) — w widgecie używamy klawiatury systemowej.
 */
import React, { useState, useEffect, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { useIsMobile } from '../hooks/useMediaQuery';

const CommunicatorTab = lazy(() => import('./tabs/CommunicatorTab'));

const LOCATION_LABEL = {
  krakow: 'Kraków',
  nowysacz: 'Nowy Sącz',
  grodek: 'Gródek n/D',
};
const LOCATION_ICON = {
  krakow: '🏰',
  nowysacz: '🏔️',
  grodek: '🏞️',
};

const FAB_SIZE = 60;
const FAB_BOTTOM = 24;
const FAB_RIGHT = 24;

// Akcent — kolor marki MR JERRY (złoto). Pasuje do nagłówków aplikacji.
const accent = 'var(--gold, #d4af37)';
const accentSolid = '#d4af37';

// ===================== FAB =====================
const FAB = ({ open, onClick, unreadCount }) => (
  <button
    onClick={onClick}
    aria-label={open ? 'Zamknij wiadomości' : 'Otwórz wiadomości'}
    data-testid="communicator-fab"
    style={{
      position: 'fixed',
      bottom: FAB_BOTTOM,
      right: FAB_RIGHT,
      width: FAB_SIZE,
      height: FAB_SIZE,
      borderRadius: '50%',
      background: `linear-gradient(135deg, ${accentSolid}, #b8941f)`,
      color: '#1a1a1a',
      border: 'none',
      cursor: 'pointer',
      boxShadow: '0 8px 24px rgba(212, 175, 55, 0.45), 0 2px 6px rgba(0,0,0,0.25)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 26,
      zIndex: 9998,
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    }}
    onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.06)'; }}
    onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
  >
    <span aria-hidden style={{
      display: 'inline-block',
      transition: 'transform 0.3s ease',
      transform: open ? 'rotate(-180deg)' : 'rotate(0)',
      lineHeight: 1,
    }}>
      {open ? '⌄' : '💬'}
    </span>
    {!open && unreadCount > 0 && (
      <span
        data-testid="communicator-fab-badge"
        style={{
          position: 'absolute',
          top: 2,
          right: 2,
          minWidth: 22,
          height: 22,
          padding: '0 6px',
          borderRadius: 11,
          background: '#ef4444',
          color: '#fff',
          fontSize: 11,
          fontWeight: 800,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '2px solid var(--bg-card)',
          boxSizing: 'border-box',
        }}
      >
        {unreadCount > 99 ? '99+' : unreadCount}
      </span>
    )}
  </button>
);

// ===================== PANEL HEADER =====================
const PanelHeader = ({ title, onClose }) => (
  <div
    style={{
      flex: '0 0 auto',
      padding: '18px 22px',
      borderBottom: '1px solid var(--border)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      background: 'var(--bg-card)',
    }}
  >
    <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>{title}</h2>
    <button
      onClick={onClose}
      aria-label="Zamknij"
      data-testid="communicator-widget-close"
      style={{
        background: 'transparent',
        border: 'none',
        color: 'var(--text-muted)',
        fontSize: 22,
        cursor: 'pointer',
        padding: 4,
        lineHeight: 1,
      }}
    >
      ✕
    </button>
  </div>
);

// ===================== BOTTOM NAV =====================
const BottomNav = ({ section, setSection, unreadCount }) => {
  const items = [
    { id: 'home', icon: '🏠', label: 'Główna' },
    { id: 'messages', icon: '💬', label: 'Wiadomości', badge: unreadCount },
    { id: 'help', icon: '❓', label: 'Pomoc' },
  ];
  return (
    <div
      style={{
        flex: '0 0 auto',
        display: 'flex',
        borderTop: '1px solid var(--border)',
        background: 'var(--bg-card)',
      }}
      data-testid="communicator-widget-bottom-nav"
    >
      {items.map((it) => {
        const active = section === it.id;
        return (
          <button
            key={it.id}
            onClick={() => setSection(it.id)}
            data-testid={`communicator-widget-nav-${it.id}`}
            style={{
              flex: 1,
              padding: '12px 8px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 4,
              color: active ? accentSolid : 'var(--text-muted)',
              position: 'relative',
              transition: 'color 0.15s ease',
            }}
          >
            <span style={{ fontSize: 22, lineHeight: 1 }}>{it.icon}</span>
            <span style={{ fontSize: 12, fontWeight: active ? 700 : 500 }}>{it.label}</span>
            {it.badge > 0 && (
              <span
                style={{
                  position: 'absolute',
                  top: 6,
                  right: '30%',
                  minWidth: 18,
                  height: 18,
                  padding: '0 5px',
                  borderRadius: 9,
                  background: '#ef4444',
                  color: '#fff',
                  fontSize: 10,
                  fontWeight: 800,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1.5px solid var(--bg-card)',
                  boxSizing: 'border-box',
                }}
              >
                {it.badge > 99 ? '99+' : it.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

// ===================== HOME SECTION =====================
const HomeSection = ({ user, location, setSection }) => {
  const hour = new Date().getHours();
  const greeting = hour < 5 ? 'Dobranoc' : hour < 12 ? 'Dzień dobry' : hour < 18 ? 'Witaj' : 'Dobry wieczór';
  const firstName = user?.firstName || user?.username || 'gościu';
  const locName = LOCATION_LABEL[location] || location;

  return (
    <div style={{ padding: 22, color: 'var(--text)' }} data-testid="communicator-widget-home">
      <div
        style={{
          background: `linear-gradient(135deg, ${accentSolid}, #b8941f)`,
          color: '#1a1a1a',
          borderRadius: 14,
          padding: '22px 22px',
          marginBottom: 18,
        }}
      >
        <div style={{ fontSize: 13, opacity: 0.8, fontWeight: 600 }}>
          {LOCATION_ICON[location]} {locName}
        </div>
        <div style={{ fontSize: 24, fontWeight: 800, marginTop: 6 }}>
          {greeting}, {firstName} 👋
        </div>
        <div style={{ fontSize: 13, marginTop: 10, lineHeight: 1.5, opacity: 0.85 }}>
          Tu wymieniasz się wiadomościami z zespołem i znajdziesz pomoc do aplikacji.
        </div>
      </div>

      <button
        onClick={() => setSection('messages')}
        data-testid="communicator-widget-go-messages"
        style={{
          width: '100%',
          background: `linear-gradient(135deg, ${accentSolid}, #b8941f)`,
          color: '#1a1a1a',
          border: 'none',
          borderRadius: 12,
          padding: '14px 16px',
          fontSize: 14,
          fontWeight: 700,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          boxShadow: '0 4px 12px rgba(212,175,55,0.3)',
          marginBottom: 10,
        }}
      >
        💬 Otwórz wiadomości
      </button>

      <button
        onClick={() => setSection('help')}
        data-testid="communicator-widget-go-help"
        style={{
          width: '100%',
          background: 'var(--bg)',
          color: 'var(--text)',
          border: '1px solid var(--border)',
          borderRadius: 12,
          padding: '14px 16px',
          fontSize: 14,
          fontWeight: 600,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
        }}
      >
        ❓ Pomoc i FAQ
      </button>
    </div>
  );
};

// ===================== HELP SECTION =====================
const FAQ_ITEMS = [
  {
    q: 'Jak dodać napiwek?',
    a: 'Wybierz zakładkę „💰 Dodaj napiwek" w menu, wypełnij datę, kelnera i kwotę, a następnie kliknij „Zapisz". Po godzinie 0:00 dzień można zamknąć dla rozliczenia miesięcznego.',
  },
  {
    q: 'Jak wypełnić raport dnia (Manager Performance)?',
    a: 'Otwórz „🎯 Manager Performance" → „Mój raport". W trakcie zmiany spisuj wydarzenia w polu tekstowym (autosave co 0.8 s). Na koniec dnia kliknij „🔒 Zakończ dzień i pokaż wskazówki AI", aby uruchomić analizę Claude Sonnet 4.5.',
  },
  {
    q: 'Jak działa Check Lista?',
    a: 'Manager dodaje codzienne zadania w „Manager Performance → Check lista → Szablon zadań". Kierownik na zmianie widzi je w „Mój raport" i odznacza w trakcie dnia. Właściciel śledzi % wykonania w Panelu właściciela.',
  },
  {
    q: 'Jak wysłać wiadomość PILNĄ do zespołu?',
    a: 'Otwórz „💬 Wiadomości" w tym widgecie, wybierz piętro/lokalizację, zaznacz „🚨 PILNE" i wyślij. Wiadomość pojawi się na czerwono, dotrze do wszystkich obecnych i wyśle push-powiadomienie.',
  },
  {
    q: 'Jak zmienić uprawnienia pracownika?',
    a: 'Otwórz „⚙️ Ustawienia" → „Użytkownicy" → wybierz pracownika → „Uprawnienia". Zaznacz konkretne moduły (kropki = aktywne). Aby cofnąć, odznacz pole.',
  },
  {
    q: 'Jak wykonać inwentaryzację?',
    a: 'Otwórz „📦 Inwentaryzacja" → „➕ Nowa sesja" → wybierz bazę (BAR/KUCHNIA), dodaj lokalizacje magazynowe i licz produkty wg metody. Auto-zapis działa nawet offline.',
  },
  {
    q: 'Nie widzę zakładki/modułu — co robić?',
    a: 'To znaczy, że nie masz uprawnień do tej zakładki. Poproś managera o nadanie odpowiedniego uprawnienia w „Ustawienia → Użytkownicy". Manager/admin ma pełny dostęp.',
  },
  {
    q: 'Jak zainstalować aplikację na telefonie?',
    a: 'Otwórz aplikację w przeglądarce (Chrome/Edge) — pojawi się prompt „Dodaj do ekranu głównego". Na iPhone otwórz w Safari → ikona udostępniania → „Dodaj do ekranu głównego".',
  },
];

const HelpSection = () => {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <div style={{ padding: 22, color: 'var(--text)' }} data-testid="communicator-widget-help">
      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.5 }}>
        ❓ Najczęściej zadawane pytania o aplikację MR JERRY. Kliknij pytanie, aby zobaczyć odpowiedź.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {FAQ_ITEMS.map((it, i) => {
          const open = openIdx === i;
          return (
            <div
              key={i}
              style={{
                background: open ? 'rgba(212, 175, 55, 0.08)' : 'var(--bg)',
                border: `1px solid ${open ? accentSolid : 'var(--border)'}`,
                borderRadius: 12,
                overflow: 'hidden',
                transition: 'all 0.15s ease',
              }}
              data-testid={`communicator-widget-faq-${i}`}
            >
              <button
                onClick={() => setOpenIdx(open ? -1 : i)}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  background: 'transparent',
                  border: 'none',
                  padding: '12px 14px',
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                  color: open ? accentSolid : 'var(--text)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 10,
                  fontFamily: 'inherit',
                }}
              >
                <span>{it.q}</span>
                <span style={{ fontSize: 16, transition: 'transform 0.2s ease', transform: open ? 'rotate(180deg)' : 'rotate(0)' }}>▾</span>
              </button>
              {open && (
                <div style={{ padding: '0 14px 14px', fontSize: 13, lineHeight: 1.6, color: 'var(--text)', opacity: 0.85 }}>
                  {it.a}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div style={{
        marginTop: 18, padding: 14,
        background: 'rgba(245, 158, 11, 0.1)',
        border: '1px solid rgba(245, 158, 11, 0.4)',
        borderRadius: 12,
        fontSize: 13, color: 'var(--text)', lineHeight: 1.5,
      }}>
        💡 <strong>Nie znalazłeś odpowiedzi?</strong> Otwórz „Zespół rozwoju" w sidebarze i zgłoś problem lub pomysł — odpowiemy w aplikacji.
      </div>
    </div>
  );
};

// ===================== MESSAGES SECTION =====================
const MessagesSection = ({ user, location }) => (
  <div
    style={{ background: 'var(--bg)', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}
    data-testid="communicator-widget-messages"
  >
    <Suspense fallback={<div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}>⏳ Ładowanie…</div>}>
      <CommunicatorTab user={user} location={location} canManage={false} hideKeyboardToggle={true} embedded={true} />
    </Suspense>
  </div>
);

// ===================== MAIN WIDGET =====================
const CommunicatorWidget = ({ user, location, unreadCount = 0 }) => {
  const [open, setOpen] = useState(false);
  const [section, setSection] = useState('messages');
  const isMobile = useIsMobile();

  // ESC zamyka panel
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const panel = open ? (
    <div
      data-testid="communicator-widget-panel"
      style={{
        position: 'fixed',
        bottom: isMobile ? 0 : FAB_BOTTOM + FAB_SIZE + 12,
        right: isMobile ? 0 : FAB_RIGHT,
        left: isMobile ? 0 : 'auto',
        top: isMobile ? 0 : 'auto',
        width: isMobile ? '100%' : 420,
        height: isMobile ? '100%' : 'min(640px, calc(100vh - 120px))',
        maxHeight: isMobile ? '100%' : 640,
        background: 'var(--bg-card)',
        borderRadius: isMobile ? 0 : 18,
        boxShadow: isMobile ? 'none' : '0 24px 60px rgba(0,0,0,0.45), 0 4px 12px rgba(0,0,0,0.2)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        zIndex: 9999,
        animation: 'commWidgetSlideUp 0.22s ease-out',
        border: isMobile ? 'none' : '1px solid var(--border)',
      }}
    >
      <PanelHeader
        title={section === 'home' ? 'Cześć!' : section === 'help' ? 'Pomoc' : 'Wiadomości'}
        onClose={() => setOpen(false)}
      />
      <div
        style={{
          flex: '1 1 auto',
          overflowY: section === 'messages' ? 'hidden' : 'auto',
          background: 'var(--bg)',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        {section === 'home' && (
          <HomeSection user={user} location={location} setSection={setSection} />
        )}
        {section === 'messages' && (
          <MessagesSection user={user} location={location} />
        )}
        {section === 'help' && <HelpSection />}
      </div>
      <BottomNav section={section} setSection={setSection} unreadCount={unreadCount} />
    </div>
  ) : null;

  return (
    <>
      <style>{`
        @keyframes commWidgetSlideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
      <FAB open={open} onClick={() => setOpen((o) => !o)} unreadCount={unreadCount} />
      {panel && createPortal(panel, document.body)}
    </>
  );
};

export default CommunicatorWidget;
